import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faPalette,
    faRulerCombined,
    faBoxes as faBoxesIcon,
    faInfoCircle,
    faHistory,
    faStore,
    faExchangeAlt,
    faShoppingCart,
    faTruck,
    faShip,
    faRoad,
    faMoneyBillWave,
    faUniversity,
    faMoneyCheck,
    faCreditCard,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faGlobe,
    faClock,
    faCalendarCheck,
    faFileInvoice,
    faReceipt,
    faMoneyBill,
    faDownload as faDownloadIcon,
    faComment,
    faCheck,
    faUndo,
    faArrowLeft,
    faArrowRight,
    faPlusCircle,
    faMinusCircle,
    faListOl,
    faCubes as faCubesIcon
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createPlaceOrderListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_placeorder_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Place Order List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_placeorder_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        placeOrderList: 'Fabric Purchase Orders List',
        allPlaceOrders: 'My Fabric Purchase Orders',
        purchaseOrders: 'Fabric Purchase Orders',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search fabric purchase orders...',
        refresh: 'Refresh',
        loadingPlaceOrders: 'Loading fabric purchase orders...',
        noPlaceOrdersFound: 'No fabric purchase orders found',
        orderDetails: 'Fabric Purchase Order Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        orderNumber: 'Order #',
        orderDate: 'Order Date',
        expectedDelivery: 'Expected Delivery',
        supplier: 'Supplier',
        supplierName: 'Supplier Name',
        company: 'Company',
        totalAmount: 'Total Amount',
        paidAmount: 'Paid',
        balance: 'Balance',
        status: 'Status',
        approveStatus: 'Approve Status',
        approved: 'Approved',
        notApproved: 'Not Approved',
        rejected: 'Rejected',
        pending: 'Pending',
        approveStatusFilter: 'Approve Status',
        paymentMethod: 'Payment',
        containerNo: 'Container',
        routeDetails: 'Route',
        itemsCount: 'Items',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        currency: 'Currency',
        exchangeRate: 'Exchange Rate',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        approve: 'Approve',
        reject: 'Reject',
        deleteConfirm: 'Are you sure you want to delete this purchase order?',
        deletedSuccess: 'Purchase order deleted successfully',
        deleteError: 'Error deleting purchase order',
        networkError: 'Network error occurred',
        orderId: 'Order ID',
        supplierInfo: 'Supplier Information',
        paymentInfo: 'Payment Information',
        shippingInfo: 'Shipping Information',
        productDetails: 'Fabric Product Details',
        productName: 'Fabric Name',
        colorCode: 'Color Code',
        type: 'Fabric Type',
        brand: 'Brand',
        batchNo: 'Batch Number',
        price: 'Price',
        quantity: 'Quantity',
        total: 'Total',
        totalYards: 'Total Yards',
        totalMeters: 'Total Meters',
        totalRolls: 'Total Rolls',
        unit: 'Unit',
        orderQuantity: 'Order Quantity',
        orderRolls: 'Order Rolls',
        notProvided: 'Not provided',
        inStock: 'In Stock',
        lowStock: 'Low Stock',
        outOfStock: 'Out of Stock',
        orderPlaced: 'Order Placed',
        processing: 'Processing',
        shipped: 'Shipped',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        other: 'Other',
        transfer: 'Transfer (Transaction OUT)',
        showing: 'Showing',
        of: 'of',
        orders: 'orders',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        orderNumberFilter: 'Order Number',
        supplierFilter: 'Supplier',
        statusFilter: 'Status',
        paymentMethodFilter: 'Payment Method',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',
        editOrder: 'Edit Fabric Purchase Order',
        updateOrder: 'Update Order',
        updating: 'Updating...',
        orderUpdated: 'Purchase order updated successfully',
        updateError: 'Error updating purchase order',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the fabric purchase order details below',
        supplierRequired: 'Supplier is required',
        orderDateRequired: 'Order date is required',
        statusRequired: 'Status is required',
        itemsRequired: 'At least one fabric item is required',
        invalidAmount: 'Please enter a valid amount',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this purchase order?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your fabric purchase order data...',
        noUserRecords: 'No fabric purchase orders found in this shop',
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        purchaseOrderReport: 'Fabric Purchase Order Report',
        orderSummary: 'Order Summary',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalOrders: 'Total Orders',
        pendingAmount: 'Pending Amount',
        totalValue: 'Total Value',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        remarks: 'Remarks',
        bankDetails: 'Bank Details',
        chequeDetails: 'Cheque Details',
        cardDetails: 'Card Details',
        baseCurrency: 'Base Currency',
        convertedAmount: 'Converted Amount',
        originalCurrency: 'Original Currency',
        yards: 'Yards',
        meters: 'Meters',
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        orderInfo: 'Order Information',
        supplierDetails: 'Supplier Details',
        shippingDetails: 'Shipping Details',
        paymentDetails: 'Payment Details',
        itemDetails: 'Fabric Item Details',
        addItem: 'Add Fabric Item',
        removeItem: 'Remove Item',
        itemName: 'Fabric Name',
        itemColor: 'Color Code',
        itemType: 'Fabric Type',
        itemBrand: 'Brand',
        itemPrice: 'Price per Yard',
        itemQuantity: 'Quantity',
        itemTotal: 'Total',
        itemUnit: 'Unit',
        itemTotalYards: 'Total Yards',
        itemTotalMeters: 'Total Meters',
        itemTotalRolls: 'Total Rolls',
        itemBatchNo: 'Batch Number',
        selectUnit: 'Select Unit',
        approveConfirm: 'Are you sure you want to approve this order?',
        approveWarning: 'Approving this order will add all fabric items to your inventory.',
        approveSuccess: 'Order approved and fabric items added to inventory successfully',
        approveError: 'Error approving order',
        alreadyApproved: 'This order is already approved',
        productAdded: 'Fabric added to inventory',
        productUpdateError: 'Error adding fabric to inventory',
        batchExists: 'Fabric found with same name/color. Stock will be added to existing product.',
        cotton: 'Cotton',
        silk: 'Silk',
        wool: 'Wool',
        linen: 'Linen',
        polyester: 'Polyester',
        blend: 'Blend',
        other: 'Other',
        fromBankName: 'From Bank Name',
        fromAccountNumber: 'From Account Number',
        toBankName: 'To Bank Name',
        toAccountNumber: 'To Account Number',
        bankReference: 'Reference / UTR Number',
        paymentReceipt: 'Payment Receipt',
        chequeNumber: 'Cheque Number',
        chequeDate: 'Cheque Date',
        chequeBankName: 'Cheque Bank Name',
        chequeBranch: 'Cheque Branch',
        chequeImage: 'Cheque Image',
        cardLastFour: 'Card Last 4 Digits',
        cardHolderName: 'Card Holder Name',
        cardExpiry: 'Card Expiry Date',
        cardTransactionId: 'Transaction ID',
        billReceipt: 'Bill / Invoice Receipt',
        orderReceipt: 'Order Receipt',
        approveItem: 'Approve Item',
        approvalQuantity: 'Approval Quantity',
        approvalRolls: 'Approval Rolls',
        ordered: 'Ordered',
        alreadyApproved: 'Already Approved',
        remainingToApprove: 'Remaining to Approve',
        fullyApproved: 'Fully Approved',
        partiallyApproved: 'Partially Approved',
        notApproved: 'Not Approved'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const placeOrderListTranslator = createPlaceOrderListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            case 'info':
                return faInfoCircle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            case 'info':
                return 'Info';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Approval Modal Component
const ApprovalModal = ({ isOpen, onClose, item, onApprove, translations, loading }) => {
    const [approvalQuantity, setApprovalQuantity] = useState('');
    const [approvalRolls, setApprovalRolls] = useState('');

    React.useEffect(() => {
        if (isOpen && item) {
            setApprovalQuantity('');
            setApprovalRolls('');
        }
    }, [isOpen, item]);

    if (!isOpen || !item) return null;

    const orderedQuantity = parseFloat(item.order_quantity) || 0;
    const orderedRolls = parseInt(item.order_rolls) || 0;
    const alreadyApprovedQty = parseFloat(item.approved_quantity) || 0;
    const alreadyApprovedRolls = parseInt(item.approved_rolls) || 0;
    const remainingQty = orderedQuantity - alreadyApprovedQty;
    const remainingRolls = orderedRolls - alreadyApprovedRolls;

    const handleSubmit = () => {
        const qty = parseFloat(approvalQuantity);
        if (isNaN(qty) || qty <= 0) {
            alert('Please enter a valid approval quantity');
            return;
        }
        if (qty > remainingQty) {
            alert(`Approval quantity cannot exceed remaining quantity (${remainingQty})`);
            return;
        }
        
        let rolls = parseInt(approvalRolls);
        if (isNaN(rolls) || rolls <= 0) {
            // Calculate rolls proportionally if not provided
            rolls = Math.max(1, Math.round((qty / orderedQuantity) * orderedRolls));
        }
        if (rolls > remainingRolls && remainingRolls > 0) {
            alert(`Approval rolls cannot exceed remaining rolls (${remainingRolls})`);
            return;
        }
        
        onApprove({
            quantity: qty,
            rolls: rolls,
            is_partial: qty < remainingQty
        });
    };

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern approve-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header-modern">
                    <div className="modal-icon"><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} /></div>
                    <div className="modal-title">
                        <h3>{translations.approveItem || 'Approve Item'}</h3>
                    </div>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="modal-body-modern">
                    <div className="approval-item-summary" style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <p><strong>{translations.productName || 'Product'}:</strong> {item.product_name}</p>
                        {item.product_colorcode && (
                            <p><strong>{translations.colorCode || 'Color'}:</strong> <span style={{ backgroundColor: item.product_colorcode, padding: '2px 8px', borderRadius: '4px' }}>{item.product_colorcode}</span></p>
                        )}
                        <p><strong>{translations.ordered || 'Ordered'}:</strong> {orderedQuantity} {item.unit || 'yards'} / {orderedRolls} rolls</p>
                        <p><strong>{translations.alreadyApproved || 'Already Approved'}:</strong> {alreadyApprovedQty} {item.unit || 'yards'} / {alreadyApprovedRolls} rolls</p>
                        <p><strong>{translations.remainingToApprove || 'Remaining to Approve'}:</strong> <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{remainingQty} {item.unit || 'yards'} / {remainingRolls} rolls</span></p>
                    </div>
                    
                    <div className="approval-form">
                        <div className="form-group">
                            <label>{translations.approvalQuantity || 'Approval Quantity'} ({item.unit || 'yards'}):</label>
                            <input
                                type="number"
                                value={approvalQuantity}
                                onChange={(e) => setApprovalQuantity(e.target.value)}
                                placeholder={`Max: ${remainingQty}`}
                                step="0.01"
                                min="0"
                                max={remainingQty}
                                className="approval-input"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                autoFocus
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>{translations.approvalRolls || 'Approval Rolls'}:</label>
                            <input
                                type="number"
                                value={approvalRolls}
                                onChange={(e) => setApprovalRolls(e.target.value)}
                                placeholder={`Max: ${remainingRolls} (leave empty for auto-calculate)`}
                                step="1"
                                min="0"
                                max={remainingRolls}
                                className="approval-input"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                        </div>
                        
                        <div className="approval-note" style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                            <small>Note: If you leave rolls empty, it will be calculated proportionally based on the ordered quantity.</small>
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer-modern">
                    <button type="button" className="btn-modern btn-secondary-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} /> {translations.cancel || 'Cancel'}
                    </button>
                    <button type="button" className="btn-modern btn-success-modern" onClick={handleSubmit} disabled={loading}>
                        {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> {translations.updating || 'Processing...'}</> : <><FontAwesomeIcon icon={faCheck} /> {translations.approve || 'Approve'}</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Order Items Table Component for View Modal
const OrderItemsTable = ({ items, currencySymbol = 'Rs', translations }) => {
    if (!items || items.length === 0) {
        return (
            <div className="no-items-message">
                <p>No fabric items found for this order</p>
            </div>
        );
    }

    const totalOrderQuantity = items.reduce((sum, item) => sum + (parseFloat(item.order_quantity) || 0), 0);
    const totalOrderRolls = items.reduce((sum, item) => sum + (parseInt(item.order_rolls) || 0), 0);
    const totalApprovedQuantity = items.reduce((sum, item) => sum + (parseFloat(item.approved_quantity) || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0);

    const unitOptions = {
        yards: 'Yards',
        meters: 'Meters'
    };

    const getApprovalStatusBadge = (status) => {
        switch (status) {
            case 'Fully Approved': return { bg: '#10b98120', color: '#10b981', text: 'Fully Approved' };
            case 'Partially Approved': return { bg: '#f59e0b20', color: '#f59e0b', text: 'Partially Approved' };
            case 'Pending': return { bg: '#ef444420', color: '#ef4444', text: 'Pending' };
            default: return { bg: '#6b728020', color: '#6b7280', text: status || 'Pending' };
        }
    };

    return (
        <div className="order-items-table-container">
            <table className="order-items-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{translations.productName || 'Fabric Name'}</th>
                        <th>{translations.colorCode || 'Color Code'}</th>
                        <th>{translations.type || 'Fabric Type'}</th>
                        <th>{translations.unit || 'Unit'}</th>
                        <th>{translations.orderQuantity || 'Order Qty'}</th>
                        <th>{translations.orderRolls || 'Order Rolls'}</th>
                        <th>{translations.approved || 'Approved Qty'}</th>
                        <th>{translations.approvalStatus || 'Status'}</th>
                        <th>{translations.price || 'Price'}</th>
                        <th>{translations.total || 'Total'}</th>
                        <th>{translations.actions || 'Actions'}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => {
                        const orderQuantity = parseFloat(item.order_quantity) || 0;
                        const orderRolls = parseInt(item.order_rolls) || 0;
                        const approvedQuantity = parseFloat(item.approved_quantity) || 0;
                        const price = parseFloat(item.price) || 0;
                        const total = parseFloat(item.total_amount) || (price * orderQuantity) || 0;
                        const unit = item.unit || 'yards';
                        const approvalStatus = item.approval_status || 'Pending';
                        const statusStyle = getApprovalStatusBadge(approvalStatus);
                        const remainingQty = orderQuantity - approvedQuantity;
                        
                        return (
                            <tr key={item.id || index}>
                                <td>{index + 1}</td>
                                <td>
                                    {item.product_name}
                                    {item.product_colorcode && (
                                        <span
                                            className="color-badge"
                                            style={{ backgroundColor: item.product_colorcode }}
                                            title={item.product_colorcode}
                                        ></span>
                                    )}
                                </td>
                                <td>{item.product_colorcode || '-'}</td>
                                <td>
                                    <span className="type-badge">
                                        {translations[item.type] || item.type || '-'}
                                    </span>
                                </td>
                                <td>{unitOptions[unit] || unit}</td>
                                <td><strong>{orderQuantity.toFixed(2)}</strong></td>
                                <td>{orderRolls}</td>
                                <td>{approvedQuantity > 0 ? approvedQuantity.toFixed(2) : '-'}</td>
                                <td>
                                    <div className="status-badge" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}>
                                        {statusStyle.text}
                                        {remainingQty > 0 && remainingQty < orderQuantity && (
                                            <span style={{ fontSize: '10px', marginLeft: '5px' }}>({remainingQty.toFixed(2)} left)</span>
                                        )}
                                    </div>
                                </td>
                                <td>{currencySymbol} {price.toFixed(2)}</td>
                                <td className="total-amount">{currencySymbol} {total.toFixed(2)}</td>
                                <td>
                                    {approvalStatus !== 'Fully Approved' && (
                                        <button 
                                            className="action-btn-modern approve-btn-modern"
                                            onClick={() => item.onApprove && item.onApprove(item)}
                                            title={translations.approve || 'Approve'}
                                        >
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="5" className="text-right"><strong>{translations.totalAmount || 'Total Amount'}:</strong></td>
                        <td><strong>{totalOrderQuantity.toFixed(2)}</strong></td>
                        <td><strong>{totalOrderRolls}</strong></td>
                        <td><strong>{totalApprovedQuantity.toFixed(2)}</strong></td>
                        <td colSpan="3"></td>
                        <td className="grand-total">{currencySymbol} {totalAmount.toFixed(2)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Edit Order Items Table Component
const EditOrderItemsTable = ({ items, currencySymbol = 'Rs', translations, onItemChange, onRemoveItem }) => {
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...items];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        if (field === 'order_quantity') {
            const orderQuantity = parseFloat(value) || 0;
            updatedItems[index].total_yards = orderQuantity;
            updatedItems[index].total_meters = orderQuantity * 0.9144;
            const price = parseFloat(updatedItems[index].price) || 0;
            updatedItems[index].total_amount = (orderQuantity * price).toFixed(2);
        }
        if (field === 'price') {
            const price = parseFloat(value) || 0;
            const orderQuantity = parseFloat(updatedItems[index].order_quantity) || 0;
            updatedItems[index].total_amount = (orderQuantity * price).toFixed(2);
        }

        onItemChange(updatedItems);
    };

    const typeOptions = [
        { value: '', label: 'Select Type' },
        { value: 'cotton', label: 'Cotton' },
        { value: 'silk', label: 'Silk' },
        { value: 'wool', label: 'Wool' },
        { value: 'linen', label: 'Linen' },
        { value: 'polyester', label: 'Polyester' },
        { value: 'blend', label: 'Blend' },
        { value: 'other', label: 'Other' }
    ];

    const unitOptions = [
        { value: 'yards', label: 'Yards' },
        { value: 'meters', label: 'Meters' }
    ];

    if (!items || items.length === 0) {
        return (
            <div className="no-items-message">
                <p>No fabric items added to this order</p>
            </div>
        );
    }

    return (
        <div className="edit-items-table-container">
            <table className="edit-items-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{translations.productName || 'Fabric Name'} *</th>
                        <th>{translations.colorCode || 'Color Code'}</th>
                        <th>{translations.type || 'Fabric Type'}</th>
                        <th>{translations.brand || 'Brand'}</th>
                        <th>{translations.unit || 'Unit'}</th>
                        <th>{translations.orderQuantity || 'Order Qty'} *</th>
                        <th>{translations.orderRolls || 'Order Rolls'}</th>
                        <th>{translations.price || 'Price'} *</th>
                        <th>{translations.total || 'Total'}</th>
                        <th>{translations.actions || 'Actions'}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={item.id || index}>
                            <td>{index + 1}</td>
                            <td>
                                <input
                                    type="text"
                                    value={item.product_name || ''}
                                    onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                                    className="edit-item-input"
                                    placeholder="Fabric name"
                                    required
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    value={item.product_colorcode || ''}
                                    onChange={(e) => handleItemChange(index, 'product_colorcode', e.target.value)}
                                    className="edit-item-input small"
                                    placeholder="Color code"
                                />
                            </td>
                            <td>
                                <select
                                    value={item.type || ''}
                                    onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                                    className="edit-item-select"
                                >
                                    {typeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {translations[opt.value] || opt.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input
                                    type="text"
                                    value={item.brand || ''}
                                    onChange={(e) => handleItemChange(index, 'brand', e.target.value)}
                                    className="edit-item-input"
                                    placeholder="Brand name"
                                />
                            </td>
                            <td>
                                <select
                                    value={item.unit || 'yards'}
                                    onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                    className="edit-item-select small"
                                >
                                    {unitOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {translations[opt.value] || opt.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={item.order_quantity || ''}
                                    onChange={(e) => handleItemChange(index, 'order_quantity', e.target.value)}
                                    className="edit-item-input small"
                                    placeholder="Order quantity"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={item.order_rolls || ''}
                                    onChange={(e) => handleItemChange(index, 'order_rolls', e.target.value)}
                                    className="edit-item-input small"
                                    placeholder="Number of rolls"
                                    min="0"
                                    step="1"
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={item.price || ''}
                                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                    className="edit-item-input small"
                                    placeholder="Price"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </td>
                            <td>
                                <span className="item-total">
                                    {currencySymbol} {parseFloat(item.total_amount || 0).toFixed(2)}
                                </span>
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => onRemoveItem(index)}
                                    className="remove-item-btn"
                                    title={translations.removeItem || 'Remove item'}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="9" className="text-right">{translations.totalAmount || 'Total Amount'}:</td>
                        <td className="grand-total">
                            {currencySymbol} {items.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0).toFixed(2)}
                        </td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Main Component
const FetchFabOrder = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    
    // Approval modal state
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalItem, setApprovalItem] = useState(null);
    const [approvingItem, setApprovingItem] = useState(false);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: null,
        order_date: '',
        expected_delivery: '',
        order_number: '',
        supplierid: '',
        supplier_name: '',
        company: '',
        status: '',
        container_no: '',
        route_details: '',
        paid_amount: '0.00',
        payment_method: '',
        remarks: '',
        from_bank_name: '',
        from_account_number: '',
        to_bank_name: '',
        to_account_number: '',
        bank_reference: '',
        payment_receipt: null,
        cheque_number: '',
        cheque_date: '',
        cheque_bank_name: '',
        cheque_branch: '',
        cheque_image: null,
        card_last_four: '',
        card_holder_name: '',
        card_expiry: '',
        card_transaction_id: '',
        bill_receipt: null,
        order_receipt: null,
        currency_code: 'PKR',
        currency_symbol: 'Rs',
        currency_rate: 1.0000,
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Currency state
    const [currentCurrency, setCurrentCurrency] = useState(() => {
        const saved = localStorage.getItem('ourzest_currency');
        return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰' };
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });
    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterBy, setFilterBy] = useState('all');
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'Order Placed', label: 'Order Placed' },
        { value: 'Processing', label: 'Processing' },
        { value: 'Shipped', label: 'Shipped' },
        { value: 'Delivered', label: 'Delivered' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];

    const paymentMethodOptions = [
        { value: '', label: 'All Methods' },
        { value: 'Transfer (Transaction OUT)', label: 'Transfer' },
        { value: 'Cash', label: 'Cash' },
        { value: 'Bank Transfer', label: 'Bank Transfer' },
        { value: 'Cheque', label: 'Cheque' },
        { value: 'Credit Card', label: 'Credit Card' },
        { value: 'Other', label: 'Other' }
    ];

    // Calculate totals
    const totalOrders = filteredOrders.length;
    const totalAmount = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0);
    const totalPaid = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.paid_amount) || 0), 0);
    const totalBalance = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.balance) || 0), 0);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);

    const showToast = (type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: (timeLeft / 3) * 100, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);

        toastTimerRef.current = timer;
    };

    useEffect(() => {
        return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); };
    }, []);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));
            } catch (err) { console.error("Error applying theme:", err); }
        };
        applyTheme(currentTheme);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) { setCurrentTheme(theme); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [currentTheme]);

    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await placeOrderListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) setCurrentLanguage(lang);
        };
        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);
        return () => { window.removeEventListener('storage', handleLanguageChange); clearInterval(interval); };
    }, [currentLanguage]);

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        }
        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    useEffect(() => {
        if (user && user.username) {
            fetchPlaceOrders();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    useEffect(() => {
        applyFilters();
    }, [orders, searchTerm, filterBy, orderNumberFilter, supplierFilter, statusFilter, paymentMethodFilter, addedByFilter, startDate, endDate]);

    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchPlaceOrders();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">{translations.noShopSelected || 'No shop selected'}</span>
                    </div>
                    <button className="btn-modern btn-switch-shop" onClick={() => setShowShopSelector(true)}>
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        }
    };

    const fetchPlaceOrders = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available');
            setLoading(false);
            return;
        }
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setOrders(data.data || []);
                resetFilters();
            } else {
                showToast('error', data.message || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...orders];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_number?.toLowerCase().includes(term) ||
                order.supplier_name?.toLowerCase().includes(term) ||
                order.company?.toLowerCase().includes(term)
            );
        }
        if (filterBy === 'orderNumber' && orderNumberFilter.trim()) {
            filtered = filtered.filter(order => order.order_number?.toLowerCase().includes(orderNumberFilter.toLowerCase()));
        }
        if (filterBy === 'supplier' && supplierFilter.trim()) {
            filtered = filtered.filter(order => order.supplier_name?.toLowerCase().includes(supplierFilter.toLowerCase()));
        }
        if (filterBy === 'status' && statusFilter) {
            filtered = filtered.filter(order => order.status === statusFilter);
        }
        if (filterBy === 'paymentMethod' && paymentMethodFilter) {
            filtered = filtered.filter(order => order.payment_method === paymentMethodFilter);
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            filtered = filtered.filter(order => (order.added_by_username?.toLowerCase().includes(addedByFilter.toLowerCase())));
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                orderDate.setHours(0, 0, 0, 0);
                return orderDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                return orderDate <= end;
            });
        }
        setFilteredOrders(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setOrderNumberFilter('');
        setSupplierFilter('');
        setStatusFilter('');
        setPaymentMethodFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    };

    const handleSearch = () => applyFilters();

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Order Placed': return { bg: '#10b98120', color: '#10b981', border: '#10b98140' };
            case 'Processing': return { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40' };
            case 'Shipped': return { bg: '#3b82f620', color: '#3b82f6', border: '#3b82f640' };
            case 'Delivered': return { bg: '#10b98120', color: '#10b981', border: '#10b98140' };
            case 'Cancelled': return { bg: '#ef444420', color: '#ef4444', border: '#ef444440' };
            default: return { bg: '#6b728020', color: '#6b7280', border: '#6b728040' };
        }
    };

    const getApproveStatusBadge = (status) => {
        if (!status || status === 'Pending') return { bg: '#ef444420', color: '#ef4444', icon: faClock, text: 'Pending' };
        if (status === 'Partially Approved') return { bg: '#f59e0b20', color: '#f59e0b', icon: faExclamationTriangle, text: 'Partially Approved' };
        if (status === 'Fully Approved') return { bg: '#10b98120', color: '#10b981', icon: faCheckCircle, text: 'Fully Approved' };
        return { bg: '#6b728020', color: '#6b7280', icon: faInfoCircle, text: status };
    };

    // Handle item approval click
    const handleApproveItem = (item) => {
        setApprovalItem(item);
        setShowApprovalModal(true);
    };

    // Submit approval
    const handleItemApproval = async (approvalData) => {
        if (!approvalItem || !currentShop) return;
        
        setApprovingItem(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/approved_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'approve_item',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    item: approvalItem,
                    approval_quantity: approvalData.quantity,
                    approval_rolls: approvalData.rolls,
                    is_partial: approvalData.is_partial
                })
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', data.message);
                fetchPlaceOrders(); // Refresh the list
                setShowApprovalModal(false);
                setApprovalItem(null);
            } else {
                showToast('error', data.message || 'Error approving item');
            }
        } catch (error) {
            console.error('Error approving item:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setApprovingItem(false);
        }
    };

    const handleView = (order) => {
        // Attach approve handler to each item
        const orderWithApprove = {
            ...order,
            items: (order.items || []).map(item => ({
                ...item,
                onApprove: () => handleApproveItem(item)
            }))
        };
        setSelectedOrder(orderWithApprove);
        setShowViewModal(true);
    };

    const handleEdit = (order) => {
        if (order.username !== user.username) {
            showToast('error', 'You can only edit your own purchase orders');
            return;
        }
        setSelectedOrder(order);
        setEditFormData({
            id: order.id,
            order_date: order.order_date || '',
            expected_delivery: order.expected_delivery || '',
            order_number: order.order_number || '',
            supplierid: order.supplierid || '',
            supplier_name: order.supplier_name || '',
            company: order.company || '',
            status: order.status || '',
            container_no: order.container_no || '',
            route_details: order.route_details || '',
            paid_amount: order.paid_amount || '0.00',
            payment_method: order.payment_method || '',
            remarks: order.remarks || '',
            from_bank_name: order.from_bank_name || '',
            from_account_number: order.from_account_number || '',
            to_bank_name: order.to_bank_name || '',
            to_account_number: order.to_account_number || '',
            bank_reference: order.bank_reference || '',
            payment_receipt: order.payment_receipt || null,
            cheque_number: order.cheque_number || '',
            cheque_date: order.cheque_date || '',
            cheque_bank_name: order.cheque_bank_name || '',
            cheque_branch: order.cheque_branch || '',
            cheque_image: order.cheque_image || null,
            card_last_four: order.card_last_four || '',
            card_holder_name: order.card_holder_name || '',
            card_expiry: order.card_expiry || '',
            card_transaction_id: order.card_transaction_id || '',
            bill_receipt: order.bill_receipt || null,
            order_receipt: order.order_receipt || null,
            currency_code: order.currency_code || 'PKR',
            currency_symbol: order.currency_symbol || 'Rs',
            currency_rate: parseFloat(order.currency_rate) || 1.0000,
            items: (order.items || []).map(item => ({
                id: item.id,
                product_id: item.product_id || null,
                product_name: item.product_name || '',
                product_colorcode: item.product_colorcode || '',
                type: item.type || '',
                brand: item.brand || '',
                unit: item.unit || 'yards',
                order_quantity: item.order_quantity || 0,
                order_rolls: item.order_rolls || '',
                price: item.price || 0,
                total_amount: item.total_amount || 0
            }))
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleEditFormChange = (field, value) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (updatedItems) => {
        setEditFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleRemoveItem = (index) => {
        const updatedItems = editFormData.items.filter((_, i) => i !== index);
        setEditFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleAddItem = () => {
        const newItem = {
            id: null,
            product_id: null,
            product_name: '',
            product_colorcode: '',
            type: '',
            brand: '',
            unit: 'yards',
            order_quantity: '',
            order_rolls: '',
            price: 0,
            total_amount: 0
        };
        setEditFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
    };

    const validateEditForm = () => {
        const errors = {};
        if (!editFormData.order_date) errors.order_date = 'Order date is required';
        if (!editFormData.supplier_name) errors.supplier_name = 'Supplier name is required';
        if (!editFormData.status) errors.status = 'Status is required';
        if (!editFormData.items || editFormData.items.length === 0) errors.items = 'At least one fabric item is required';
        setEditFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateEditForm()) {
            showToast('error', 'Please fix the validation errors');
            return;
        }
        
        const payload = {
            action: 'edit',
            id: editFormData.id,
            username: user.username,
            shop_id: currentShop.shop_id,
            order_date: editFormData.order_date,
            expected_delivery: editFormData.expected_delivery,
            order_number: editFormData.order_number,
            supplierid: parseInt(editFormData.supplierid) || 0,
            supplier_name: editFormData.supplier_name,
            company: editFormData.company,
            status: editFormData.status,
            container_no: editFormData.container_no,
            route_details: editFormData.route_details,
            paid_amount: parseFloat(editFormData.paid_amount) || 0,
            payment_method: editFormData.payment_method,
            remarks: editFormData.remarks,
            from_bank_name: editFormData.from_bank_name || null,
            from_account_number: editFormData.from_account_number || null,
            to_bank_name: editFormData.to_bank_name || null,
            to_account_number: editFormData.to_account_number || null,
            bank_reference: editFormData.bank_reference || null,
            payment_receipt: editFormData.payment_receipt,
            cheque_number: editFormData.cheque_number || null,
            cheque_date: editFormData.cheque_date || null,
            cheque_bank_name: editFormData.cheque_bank_name || null,
            cheque_branch: editFormData.cheque_branch || null,
            cheque_image: editFormData.cheque_image,
            card_last_four: editFormData.card_last_four || null,
            card_holder_name: editFormData.card_holder_name || null,
            card_expiry: editFormData.card_expiry || null,
            card_transaction_id: editFormData.card_transaction_id || null,
            bill_receipt: editFormData.bill_receipt,
            order_receipt: editFormData.order_receipt,
            currency_code: editFormData.currency_code,
            currency_symbol: editFormData.currency_symbol,
            currency_rate: parseFloat(editFormData.currency_rate) || 1,
            items: editFormData.items.map(item => ({
                id: item.id,
                product_id: item.product_id ? parseInt(item.product_id) : null,
                product_name: item.product_name,
                product_colorcode: item.product_colorcode || null,
                type: item.type || null,
                brand: item.brand || null,
                unit: item.unit || 'yards',
                order_quantity: parseFloat(item.order_quantity) || 0,
                order_rolls: parseInt(item.order_rolls) || null,
                price: parseFloat(item.price) || 0,
                total_amount: (parseFloat(item.price) || 0) * (parseFloat(item.order_quantity) || 0)
            }))
        };
        
        setEditLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', 'Order updated successfully');
                setShowEditModal(false);
                fetchPlaceOrders();
            } else {
                showToast('error', data.message || 'Error updating order');
            }
        } catch (error) {
            console.error('Error updating order:', error);
            showToast('error', 'Error updating order');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteClick = (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (!order || order.username !== user.username) {
            showToast('error', 'You can only delete your own purchase orders');
            return;
        }
        setOrderToDelete(orderId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!orderToDelete || !currentShop) return;
        const order = orders.find(o => o.id === orderToDelete);
        if (!order || order.username !== user.username) {
            showToast('error', 'You can only delete your own purchase orders');
            setShowDeleteModal(false);
            setOrderToDelete(null);
            return;
        }
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id: orderToDelete, username: user.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', 'Purchase order deleted successfully');
                fetchPlaceOrders();
                setShowDeleteModal(false);
                setOrderToDelete(null);
            } else {
                showToast('error', data.message || 'Error deleting purchase order');
            }
        } catch (error) {
            console.error('Error deleting purchase order:', error);
            showToast('error', 'Error deleting purchase order');
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    };

    const formatNumber = (num, decimals = 2) => {
        if (num === undefined || num === null || num === '') return '';
        return parseFloat(num).toFixed(decimals).replace(/\.?0+$/, '');
    };

    const getTodayDate = () => new Date().toISOString().split('T')[0];

    useEffect(() => { if (startDate && endDateRef.current) endDateRef.current.min = startDate; }, [startDate]);
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) startDateRef.current.max = today;
        if (endDateRef.current) endDateRef.current.max = today;
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            <ShopSelectorModal isOpen={showShopSelector} onClose={() => setShowShopSelector(false)} shops={availableShops} currentShop={currentShop} onSelectShop={handleSwitchShop} translations={translations} loading={loadingShops} />
            
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                    <div className="header-text">
                        <h1>{translations.placeOrderList || 'Fabric Purchase Orders List'}</h1>
                        <div className="shop-info">
                            <p>{currentShop?.shop_name ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}` : `${translations.allPlaceOrders || 'My Purchase Orders'} - ${user?.username}`}</p>
                        </div>
                    </div>
                </div>
                {renderShopIndicator()}
            </div>

            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="orderNumber">{translations.orderNumberFilter || 'Order Number'}</option>
                            <option value="supplier">{translations.supplierFilter || 'Supplier'}</option>
                            <option value="status">{translations.statusFilter || 'Status'}</option>
                            <option value="paymentMethod">{translations.paymentMethodFilter || 'Payment Method'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>
                    {filterBy === 'orderNumber' && (<div className="filter-group compact"><input type="text" value={orderNumberFilter} onChange={(e) => setOrderNumberFilter(e.target.value)} placeholder={translations.orderNumberFilter || 'Order number...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'supplier' && (<div className="filter-group compact"><input type="text" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} placeholder={translations.supplierFilter || 'Supplier name...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'status' && (<div className="filter-group compact"><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>{statusOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></div>)}
                    {filterBy === 'paymentMethod' && (<div className="filter-group compact"><select value={paymentMethodFilter} onChange={(e) => setPaymentMethodFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>{paymentMethodOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></div>)}
                    {filterBy === 'added_by' && (<div className="filter-group compact"><input type="text" value={addedByFilter} onChange={(e) => setAddedByFilter(e.target.value)} placeholder={translations.addedByFilter || 'Added by username...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'date' && (<><div className="filter-group compact date-filter"><input ref={startDateRef} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={getTodayDate()} className="filter-input compact date-input" title={translations.startDate || 'Start Date'} /></div><div className="filter-group compact date-filter"><input ref={endDateRef} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={getTodayDate()} className="filter-input compact date-input" title={translations.endDate || 'End Date'} /></div></>)}
                    <div className="filter-group compact search-group"><div className="search-box compact"><input type="text" placeholder={translations.searchPlaceholder || 'Search fabric purchase orders...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} dir={isRTL ? 'rtl' : 'ltr'} /></div></div>
                    <div className="filter-group compact"><button onClick={fetchPlaceOrders} className="refresh-btn-modern compact" disabled={loading} title={translations.refresh || 'Refresh'}><FontAwesomeIcon icon={faRefresh} /><span className="btn-text">{translations.refresh || 'Refresh'}</span></button></div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container"><div className="loading-spinner-modern"></div><p>{translations.loadingUserData || 'Loading your fabric purchase order data...'}</p></div>
            ) : !currentShop ? (
                <div className="no-shop-container"><div className="no-shop-message"><FontAwesomeIcon icon={faStore} /><h3>{translations.noShopSelected || 'No Shop Selected'}</h3><p>Please select a shop to view fabric purchase orders</p><button className="btn-modern btn-primary-modern" onClick={() => setShowShopSelector(true)}><FontAwesomeIcon icon={faExchangeAlt} /><span>{translations.switchShop || 'Switch Shop'}</span></button></div></div>
            ) : (
                <>
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.orderNumber || 'Order #'}</th>
                                            <th className="centered-cell">{translations.orderDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.expectedDelivery || 'Exp. Delivery'}</th>
                                            <th className="centered-cell">{translations.supplierName || 'Supplier'}</th>
                                            <th className="centered-cell">{translations.company || 'Company'}</th>
                                            <th className="centered-cell">{translations.totalAmount || 'Total'}</th>
                                            <th className="centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="centered-cell">{translations.status || 'Status'}</th>
                                            <th className="centered-cell">{translations.approveStatus || 'Approve Status'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="centered-cell">{translations.containerNo || 'Container'}</th>
                                            <th className="centered-cell">{translations.itemsCount || 'Items'}</th>
                                            <th className="centered-cell">{translations.currency || 'Currency'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.map((order, index) => {
                                            const addedBy = order.added_by_username || order.username || '-';
                                            const statusStyle = getStatusBadge(order.status);
                                            const approveStatusStyle = getApproveStatusBadge(order.approve_status);
                                            const itemsCount = order.items ? order.items.length : 0;
                                            const currencySymbol = order.currency_symbol || 'Rs';
                                            return (
                                                <tr key={order.id}>
                                                    <td className="id-cell centered-cell"><span className="cell-text">{startIndex + index + 1}</span></td>
                                                    <td className="centered-cell"><span className="cell-text order-number">{order.order_number || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{formatDate(order.order_date)}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{formatDate(order.expected_delivery) || '-'}</span></td>
                                                    <td className="centered-cell"><div className="supplier-details"><span className="supplier-name">{order.supplier_name}</span></div></td>
                                                    <td className="centered-cell"><span className="cell-text">{order.company || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text total-amount">{currencySymbol} {formatNumber(order.total_amount)}</span></td>
                                                    <td className="centered-cell"><span className="cell-text paid-amount">{currencySymbol} {formatNumber(order.paid_amount)}</span></td>
                                                    <td className="centered-cell"><span className={`cell-text balance ${parseFloat(order.balance) > 0 ? 'negative' : 'positive'}`}>{currencySymbol} {formatNumber(order.balance)}</span></td>
                                                    <td className="centered-cell"><div className="status-badge" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>{order.status || '-'}</div></td>
                                                    <td className="centered-cell"><div className="status-badge" style={{ backgroundColor: approveStatusStyle.bg, color: approveStatusStyle.color }}><FontAwesomeIcon icon={approveStatusStyle.icon} size="xs" /> {approveStatusStyle.text}</div></td>
                                                    <td className="centered-cell"><span className="cell-text">{order.payment_method || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{order.container_no || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text items-count">{itemsCount}</span></td>
                                                    <td className="centered-cell"><span className="currency-badge">{order.currency_code || 'PKR'}</span></td>
                                                    <td className="centered-cell"><span className={`added-by-badge ${addedBy === user?.username ? 'self' : ''}`}><span className="cell-text">{addedBy}</span></span></td>
                                                    <td className="actions-cell-modern centered-cell"><div className="actions-content"><button onClick={() => handleView(order)} className="action-btn-modern view-btn-modern" title={translations.view || 'View'}><FontAwesomeIcon icon={faEye} /></button><button onClick={() => handleEdit(order)} className="action-btn-modern edit-btn-modern" title={translations.edit || 'Edit'} disabled={order.username !== user?.username}><FontAwesomeIcon icon={faEdit} /></button><button onClick={() => handleDeleteClick(order.id)} className="action-btn-modern delete-btn-modern" title={translations.delete || 'Delete'} disabled={order.username !== user?.username}><FontAwesomeIcon icon={faTrash} /></button></div></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    {filteredOrders.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info"><span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredOrders.length)} {translations.of || 'of'} {filteredOrders.length} {translations.orders || 'orders'}</span>{currentShop?.shop_name && (<span className="shop-info-pagination"> | {translations.shopName || 'Shop'}: {currentShop.shop_name}</span>)}</div>
                            <div className="pagination-controls"><button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn"><FontAwesomeIcon icon={faChevronLeft} /><span>{translations.previous || 'Previous'}</span></button><div className="page-numbers">{Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pageNum; if (totalPages <= 5) pageNum = i + 1; else if (currentPage <= 3) pageNum = i + 1; else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i; else pageNum = currentPage - 2 + i; return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>); })}</div><button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn"><span>{translations.next || 'Next'}</span><FontAwesomeIcon icon={faChevronRight} /></button></div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', width: '1200px' }}>
                        <div className="modal-header-modern">
                            <div className="modal-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                            <div className="modal-title">
                                <h3>{translations.orderDetails || 'Fabric Purchase Order Details'}</h3>
                                <span className="order-number-badge">#{selectedOrder.order_number}</span>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faFileInvoice} /> Order Summary</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">Order ID:</span><span className="view-value">#{selectedOrder.id}</span></div>
                                    <div className="view-row"><span className="view-label">Order Number:</span><span className="view-value">{selectedOrder.order_number}</span></div>
                                    <div className="view-row"><span className="view-label">Order Date:</span><span className="view-value">{formatDate(selectedOrder.order_date)}</span></div>
                                    <div className="view-row"><span className="view-label">Expected Delivery:</span><span className="view-value">{formatDate(selectedOrder.expected_delivery) || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">Status:</span><span className="view-value"><div className="status-badge" style={getStatusBadge(selectedOrder.status)}>{selectedOrder.status}</div></span></div>
                                </div>
                            </div>
                            
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faUser} /> Supplier Information</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">Supplier Name:</span><span className="view-value">{selectedOrder.supplier_name}</span></div>
                                    <div className="view-row"><span className="view-label">Company:</span><span className="view-value">{selectedOrder.company || '-'}</span></div>
                                </div>
                            </div>
                            
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyBillWave} /> Payment Information</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">Payment Method:</span><span className="view-value">{selectedOrder.payment_method || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">Total Amount:</span><span className="view-value total-amount">{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.total_amount)}</span></div>
                                    <div className="view-row"><span className="view-label">Paid Amount:</span><span className="view-value paid-amount">{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.paid_amount)}</span></div>
                                    <div className="view-row"><span className="view-label">Balance:</span><span className="view-value balance">{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.balance)}</span></div>
                                </div>
                            </div>
                            
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faBoxes} /> Fabric Items</h4>
                                <OrderItemsTable 
                                    items={selectedOrder.items} 
                                    currencySymbol={selectedOrder.currency_symbol || 'Rs'} 
                                    translations={translations} 
                                />
                            </div>
                            
                            {selectedOrder.remarks && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faComment} /> Remarks</h4>
                                    <div className="remarks-box">{selectedOrder.remarks}</div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', width: '1200px' }}>
                        <div className="modal-header-modern">
                            <div className="modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="modal-title">
                                <h3>{translations.editOrder || 'Edit Fabric Purchase Order'}</h3>
                                <span className="order-number-badge">#{selectedOrder.order_number}</span>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleEditSubmit}>
                            <div className="modal-body-modern edit-modal-body">
                                <div className="edit-section">
                                    <h4 className="edit-section-title"><FontAwesomeIcon icon={faFileInvoice} /> Order Information</h4>
                                    <div className="edit-grid">
                                        <div className="form-group"><label>Order Date *</label><input type="date" value={editFormData.order_date} onChange={(e) => handleEditFormChange('order_date', e.target.value)} className={editFormErrors.order_date ? 'error' : ''} max={getTodayDate()} /></div>
                                        <div className="form-group"><label>Expected Delivery</label><input type="date" value={editFormData.expected_delivery} onChange={(e) => handleEditFormChange('expected_delivery', e.target.value)} /></div>
                                        <div className="form-group"><label>Order Number</label><input type="text" value={editFormData.order_number} onChange={(e) => handleEditFormChange('order_number', e.target.value)} placeholder="Auto-generated if empty" /></div>
                                        <div className="form-group"><label>Status *</label><select value={editFormData.status} onChange={(e) => handleEditFormChange('status', e.target.value)} className={editFormErrors.status ? 'error' : ''}><option value="">Select Status</option><option value="Order Placed">Order Placed</option><option value="Processing">Processing</option><option value="Shipped">Shipped</option><option value="Delivered">Delivered</option><option value="Cancelled">Cancelled</option></select></div>
                                        <div className="form-group"><label>Payment Method</label><select value={editFormData.payment_method} onChange={(e) => handleEditFormChange('payment_method', e.target.value)}><option value="">Select Payment Method</option><option value="Transfer (Transaction OUT)">Transfer</option><option value="Cash">Cash</option><option value="Bank Transfer">Bank Transfer</option><option value="Cheque">Cheque</option><option value="Credit Card">Credit Card</option><option value="Other">Other</option></select></div>
                                        <div className="form-group"><label>Paid Amount</label><input type="number" value={editFormData.paid_amount} onChange={(e) => handleEditFormChange('paid_amount', e.target.value)} step="0.01" min="0" /></div>
                                        <div className="form-group"><label>Container No</label><input type="text" value={editFormData.container_no} onChange={(e) => handleEditFormChange('container_no', e.target.value)} /></div>
                                        <div className="form-group full-width"><label>Remarks</label><textarea value={editFormData.remarks} onChange={(e) => handleEditFormChange('remarks', e.target.value)} rows="2" /></div>
                                    </div>
                                </div>
                                
                                <div className="edit-section">
                                    <h4 className="edit-section-title"><FontAwesomeIcon icon={faUser} /> Supplier Details</h4>
                                    <div className="edit-grid">
                                        <div className="form-group"><label>Supplier Name *</label><input type="text" value={editFormData.supplier_name} onChange={(e) => handleEditFormChange('supplier_name', e.target.value)} className={editFormErrors.supplier_name ? 'error' : ''} placeholder="Supplier name" /></div>
                                        <div className="form-group"><label>Supplier ID *</label><input type="text" value={editFormData.supplierid} onChange={(e) => handleEditFormChange('supplierid', e.target.value)} className={editFormErrors.supplierid ? 'error' : ''} placeholder="Supplier ID" /></div>
                                        <div className="form-group"><label>Company</label><input type="text" value={editFormData.company} onChange={(e) => handleEditFormChange('company', e.target.value)} placeholder="Company name" /></div>
                                    </div>
                                </div>
                                
                                <div className="edit-section">
                                    <div className="section-header-with-button">
                                        <h4 className="edit-section-title"><FontAwesomeIcon icon={faBoxes} /> Fabric Item Details *</h4>
                                        <button type="button" onClick={handleAddItem} className="add-item-btn"><FontAwesomeIcon icon={faPlusCircle} /> Add Fabric Item</button>
                                    </div>
                                    <EditOrderItemsTable 
                                        items={editFormData.items} 
                                        currencySymbol={editFormData.currency_symbol || 'Rs'} 
                                        translations={translations} 
                                        onItemChange={handleItemChange} 
                                        onRemoveItem={handleRemoveItem} 
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)}><FontAwesomeIcon icon={faTimes} /> Cancel</button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? <><FontAwesomeIcon icon={faSpinner} spin /> Updating...</> : <><FontAwesomeIcon icon={faSave} /> Save Changes</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="modal-title"><h3 style={{ color: 'var(--customer-error-color)' }}>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p>{translations.deleteMessage || 'Are you sure you want to delete this purchase order?'}</p>
                                <p className="warning-text"><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /> No, Cancel</button>
                            <button type="button" className="btn-modern btn-delete-modern" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} /> Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval Modal */}
            <ApprovalModal
                isOpen={showApprovalModal}
                onClose={() => {
                    setShowApprovalModal(false);
                    setApprovalItem(null);
                }}
                item={approvalItem}
                onApprove={handleItemApproval}
                translations={translations}
                loading={approvingItem}
            />
        </div>
    );
};

export default FetchFabOrder;